import { SalarisSimulatie } from './SalarisSimulatie';
import { GeldSchakelaar } from '../lib/geld';
import { LogoMark } from './Logo';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { LogOut } from 'lucide-react';
import { NAV_GROUPS } from '../lib/nav';
import { cn } from '../lib/utils';
import { useState, useEffect, useRef } from 'react';
/* CLAIM-O */
import { BottomNav } from './BottomNav';
import { InstallPromptBanner } from './InstallPromptBanner';
/* CLAIM-K */
import { AfwijkingenButton } from './AfwijkingenButton';

export function AppLayout() {
  const { profile, email, logout } = useAuth();
  const location = useLocation();

  const { data: onboarding } = useQuery({
    queryKey: ['onboarding'],
    queryFn: api.onboarding.progress,
  });

  // Redirect to onboarding if not complete
  useEffect(() => {
    if (onboarding && !onboarding.is_completed && location.pathname !== '/onboarding') {
      window.location.href = '/onboarding';
    }
  }, [onboarding, location.pathname]);

  const initials = profile
    ? `${profile.voornaam?.[0] ?? ''}${profile.achternaam?.[0] ?? ''}`.toUpperCase() || 'R'
    : 'R';
  const name = profile ? `${profile.voornaam ?? ''} ${profile.achternaam ?? ''}`.trim() : 'Gebruiker';

  return (
    <div className="min-h-screen lg:flex">
      {/* Desktop: één vaste kolom met navigatie, afwijkingen en account */}
      <aside className="hidden lg:flex lg:flex-col w-60 shrink-0 h-screen sticky top-0 border-r border-surface-line/60 bg-slate-950/40">
        <div className="px-5 pt-6 pb-5 flex items-center justify-between">
          <Wordmark />
          <AfwijkingenButton />
        </div>
        <nav className="flex-1 overflow-y-auto px-3 space-y-5">
          {NAV_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="px-3 mb-1.5 text-xs font-medium text-slate-500">{g.title}</div>
              <div className="space-y-0.5">
                {g.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      cn(
                        'relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-surface-raised text-slate-50 before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-full before:bg-slate-100'
                          : 'text-slate-400 hover:text-slate-100 hover:bg-surface/70'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5" aria-hidden />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <AccountMenu initials={initials} name={name} email={email} onLogout={logout} placement="up" />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobiel: slanke balk */}
        <header
          className="lg:hidden sticky top-0 z-30 bg-slate-900/90 backdrop-blur border-b border-surface-line/60"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="px-4 h-14 flex items-center gap-2">
            <Wordmark />
            <div className="ml-auto flex items-center gap-1.5">
              <SalarisSimulatie compact />
              <GeldSchakelaar />
              {/* CLAIM-K */}
              <AfwijkingenButton />
              <AccountMenu initials={initials} name={name} email={email} onLogout={logout} placement="down" compact />
            </div>
          </div>
        </header>

        {/* Desktop: dunne balk met de bruto/netto-keuze rechtsboven, blijft staan bij scrollen */}
        <div className="hidden lg:flex sticky top-0 z-20 h-14 items-center justify-end gap-3 border-b border-surface-line/60 bg-slate-900/85 px-10 backdrop-blur">
          <SalarisSimulatie />
          <GeldSchakelaar />
        </div>

        <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 lg:pt-7 overflow-x-hidden pb-[calc(env(safe-area-inset-bottom)+6rem)] lg:pb-12">
          {location.pathname === '/' && (
            <div className="mb-4 lg:hidden">
              <InstallPromptBanner />
            </div>
          )}
          {/* Zachte overgang tussen pagina's (200 ms). */}
          <div key={location.pathname} className="animate-enter">
            <Outlet />
          </div>
        </main>
      </div>

      {/* CLAIM-O: PWA mobile bottom nav */}
      <BottomNav />
    </div>
  );
}

/** Schild + naam. */
function Wordmark() {
  return (
    <div className="flex items-center gap-2 select-none">
      <LogoMark className="h-6 w-5" />
      <span className="font-display text-xl font-semibold tracking-tight text-slate-50 leading-none">Trigion</span>
    </div>
  );
}

function AccountMenu(props: {
  initials: string;
  name: string;
  email: string | null | undefined;
  onLogout: () => void;
  placement: 'up' | 'down';
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className={cn('relative', !props.compact && 'p-3 border-t border-surface-line/60')} ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className={cn(
          'flex items-center gap-3 rounded-xl hover:bg-surface/70 transition-colors',
          props.compact ? 'p-1' : 'w-full px-2 py-2 text-left'
        )}
      >
        <span className="w-8 h-8 shrink-0 rounded-full bg-surface-raised border border-surface-line flex items-center justify-center text-xs font-semibold text-slate-200">
          {props.initials}
        </span>
        {!props.compact && (
          <span className="min-w-0">
            <span className="block text-sm font-medium text-slate-100 truncate">{props.name}</span>
            <span className="block text-xs text-slate-500 truncate">Beveiliger A</span>
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-40 w-64 rounded-2xl border border-surface-line bg-surface-raised shadow-2xl shadow-black/40 overflow-hidden animate-fade-in',
            props.placement === 'up' ? 'bottom-full left-3 mb-2' : 'top-full right-0 mt-2'
          )}
        >
          <div className="p-3 border-b border-surface-line">
            <div className="font-medium">{props.name}</div>
            <div className="text-xs text-slate-400 truncate">{props.email}</div>
          </div>
          <button
            onClick={props.onLogout}
            className="w-full text-left px-3 py-2.5 text-sm hover:bg-slate-700/60 flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" /> Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}
